// GAVION recruiting (spec §3): the CV of POST /jobs/applications. Strict base64, 2 MB decoded at most (owner ruling 2026-10-27), and its type
// read from its bytes (PDF signature; Word 97–2003: OLE2 with a WordDocument stream; Word 2007+: ZIP whose central
// directory has word/document.xml and [Content_Types].xml and no macros), never from the declared type
// alone: the declared type and the file extension must agree with the bytes. SQL twin of the limits:
// 91_gavion_recruiting.sql (job_applications.cv_path / cv_size, the job-cvs bucket).
export const CV_MAX_BYTES = 2 * 1024 * 1024;
export const CV_CONTENT_TYPES = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
} as const;
export type CvExt = keyof typeof CV_CONTENT_TYPES;
export type CvInput = {
  filename: string;
  content_type: string;
  base64: string;
};
export type CvChecked =
  | {
      ok: true;
      bytes: Uint8Array;
      ext: CvExt;
      contentType: (typeof CV_CONTENT_TYPES)[CvExt];
      filename: string;
    }
  | { ok: false; field: string };

// No groups (a repeated group overflows the regex stack on a 7 MB string); padding only at the end.
const BASE64 = /^[A-Za-z0-9+/]+={0,2}$/;

/** Standard base64 (RFC 4648 §4) with padding, no whitespace, no data: prefix; null otherwise or when empty. */
export function decodeBase64Strict(s: string): Uint8Array | null {
  if (!s || s.length % 4 !== 0 || !BASE64.test(s)) return null;
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

const startsWith = (b: Uint8Array, sig: readonly number[]) =>
  b.length >= sig.length && sig.every((x, i) => b[i] === x);
const PDF_SIG = [0x25, 0x50, 0x44, 0x46, 0x2d]; // %PDF-
const OLE2_SIG = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];
const ZIP_SIG = [0x50, 0x4b, 0x03, 0x04]; // PK\3\4
// Review 2026-10-27: a .docx is a ZIP whose central directory lists word/document.xml and [Content_Types].xml and no
// word/vbaProject.bin (macros: .docm); a .doc is an OLE2 compound file whose directory has a "WordDocument" stream
// (an .xls, .msg or .msi is OLE2 too, without it).
const u16 = (b: Uint8Array, at: number) => b[at] | (b[at + 1] << 8);
const u32 = (b: Uint8Array, at: number) =>
  (b[at] | (b[at + 1] << 8) | (b[at + 2] << 16) | (b[at + 3] << 24)) >>> 0;

/** The entry names of the ZIP central directory, or null when it cannot be read. */
export function zipEntryNames(b: Uint8Array): string[] | null {
  // End of central directory: 22 bytes + a comment of at most 65 535 bytes, at the end of the file.
  const stop = Math.max(0, b.length - 22 - 0xffff);
  let eocd = -1;
  for (let i = b.length - 22; i >= stop; i--)
    if (u32(b, i) === 0x06054b50) {
      eocd = i;
      break;
    }
  if (eocd < 0) return null;
  const count = u16(b, eocd + 10);
  let at = u32(b, eocd + 16);
  const names: string[] = [];
  const dec = new TextDecoder();
  for (let n = 0; n < count; n++) {
    if (at + 46 > b.length || u32(b, at) !== 0x02014b50) return null;
    const nameLen = u16(b, at + 28);
    const next = at + 46 + nameLen + u16(b, at + 30) + u16(b, at + 32);
    if (next > b.length) return null;
    names.push(dec.decode(b.subarray(at + 46, at + 46 + nameLen)));
    at = next;
  }
  return names;
}

const isDocx = (b: Uint8Array): boolean => {
  const names = zipEntryNames(b);
  return (
    names !== null &&
    names.includes("word/document.xml") &&
    names.includes("[Content_Types].xml") &&
    !names.some((n) => n.toLowerCase() === "word/vbaproject.bin")
  );
};

/** The stream / storage names of an OLE2 compound file's directory, or null when it cannot be read. */
export function oleDirectoryNames(b: Uint8Array): string[] | null {
  if (b.length < 512) return null;
  const shift = u16(b, 30);
  if (shift !== 9 && shift !== 12) return null;
  const size = 1 << shift;
  const sector = (s: number) => 512 + s * size; // the header takes 512 bytes (v3) / one sector (v4: 4096)
  const base = shift === 12 ? 4096 - 512 : 0;
  const offset = (s: number) => sector(s) + base;
  // FAT sectors listed in the header DIFAT (109 entries: enough for files far above 2 MB with 512-byte sectors)
  const fatSectors: number[] = [];
  for (let i = 0; i < 109; i++) {
    const s = u32(b, 76 + i * 4);
    if (s >= 0xfffffffa) break;
    fatSectors.push(s);
  }
  const perFat = size / 4;
  const nextOf = (s: number): number | null => {
    const fs = fatSectors[Math.floor(s / perFat)];
    if (fs === undefined) return null;
    const at = offset(fs) + (s % perFat) * 4;
    return at + 4 <= b.length ? u32(b, at) : null;
  };
  const names: string[] = [];
  let s = u32(b, 48);
  for (let guard = 0; s < 0xfffffffa && guard < 10_000; guard++) {
    const start = offset(s);
    if (start + size > b.length) return null;
    for (let e = start; e < start + size; e += 128) {
      const len = u16(b, e + 64);
      if (len >= 2 && len <= 64) {
        let name = "";
        for (let k = 0; k < len - 2; k += 2)
          name += String.fromCharCode(u16(b, e + k));
        names.push(name);
      }
    }
    const next = nextOf(s);
    if (next === null) return null;
    s = next;
  }
  return names;
}

const isDoc = (b: Uint8Array): boolean =>
  oleDirectoryNames(b)?.includes("WordDocument") ?? false;

/** The CV type from its bytes, or null (anything else: another ZIP or OLE2 file, a macro document, …). */
export function detectCvType(b: Uint8Array): CvExt | null {
  if (startsWith(b, PDF_SIG)) return "pdf";
  if (startsWith(b, OLE2_SIG)) return isDoc(b) ? "doc" : null;
  if (startsWith(b, ZIP_SIG)) return isDocx(b) ? "docx" : null;
  return null;
}

/** The file's own name (no folders), without control or format characters, 255 characters at most; null if blank. */
export function cleanCvFilename(raw: string): string | null {
  const base = raw.split(/[\\/]/).pop() ?? "";
  const clean = base.replace(/[\p{Cc}\p{Cf}]/gu, "").trim();
  if (!clean || clean === "." || clean === "..") return null;
  if (clean.length <= 255) return clean;
  const dot = clean.lastIndexOf(".");
  const ext = dot > 0 && clean.length - dot <= 10 ? clean.slice(dot) : "";
  return clean.slice(0, 255 - ext.length) + ext;
}

const extOf = (name: string): string =>
  name.includes(".") ? name.slice(name.lastIndexOf(".") + 1).toLowerCase() : "";

/** prefix: "cv" or "letter" (the field paths of a 422). */
export function checkCv(cv: CvInput, prefix = "cv"): CvChecked {
  const bytes = decodeBase64Strict(cv.base64);
  if (!bytes || bytes.length === 0 || bytes.length > CV_MAX_BYTES)
    return { ok: false, field: `${prefix}.base64` };
  const ext = detectCvType(bytes);
  if (!ext || CV_CONTENT_TYPES[ext] !== cv.content_type.toLowerCase())
    return { ok: false, field: `${prefix}.content_type` };
  const filename = cleanCvFilename(cv.filename);
  if (!filename || extOf(filename) !== ext)
    return { ok: false, field: `${prefix}.filename` };
  return { ok: true, bytes, ext, contentType: CV_CONTENT_TYPES[ext], filename };
}
